import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import {
  IActivo,
  IFilter,
  IPopulate,
  IQueryParam,
  IServicio,
  CategoriaProveedor,
  CategoriaServicio,
  ICliente,
  IProveedor,
  AccionesRol,
} from 'modelos/src';
import { Subscription } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { fechaCelda } from '../../../../auxiliares/tabla/celdas';
import { ServiciosService } from '../../../../auxiliares/servicios/http/servicios.service';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import {
  IColumnas,
  ICrearBoton,
  TablaComponent,
} from '../../../../auxiliares/tabla/tabla.component';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../auxiliares/tabla/filtro/filtro.component';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../../auxiliares/dialog/dialog.service';
import { ActivosService } from '../../../../auxiliares/servicios/http/activos.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { DetalleServicioComponent } from '../detalle-servicio/detalle-servicio.component';
import { ClienteJerarquiaService } from '../../../../auxiliares/servicios/cliente-jerarquia.service';

@Component({
  selector: 'app-listado-servicios',
  templateUrl: './listado-servicios.component.html',
  styleUrl: './listado-servicios.component.scss',
  standalone: true,
  imports: [TablaComponent, MatDialogModule],
})
export class ListadoServiciosComponent implements OnInit, OnDestroy {
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);
  private dialogService = inject(DialogService);
  private service = inject(ServiciosService);
  private navigator = inject(Router);
  private route = inject(ActivatedRoute);
  dialog = inject(MatDialog);
  activosService = inject(ActivosService);

  @Input() categoria: CategoriaProveedor = 'Vehiculo';

  /**
   * Cuando se embebe en el detalle de un activo: filtra por ese activo y el
   * alta/edición navegan a la ruta absoluta (dentro del tab no hay rutas
   * hijas 'crear'/'editar' a las que ir relativo).
   */
  @Input() idActivo?: string;

  /**
   * Cliente del activo. Va junto con `idActivo` al formulario de alta: sin el
   * cliente, el selector de vehículos del form no carga opciones y el activo
   * preseleccionado no matchea nada.
   */
  @Input() idCliente?: string;

  //// Tabla nueva
  public name = 'ListadoServiciosComponent';
  public servicios?: IServicio[];
  public activos: IActivo[] = [];
  public totalCount = 0;
  public columnas?: IColumnas<IServicio>[];

  public clientes?: ICliente[];
  public proveedores?: IProveedor[];

  //Subs
  public servicios$?: Subscription;
  public proveedores$?: Subscription;
  //
  //Permisos
  private puedeCrear: AccionesRol;
  private puedeEditar: AccionesRol;
  private puedeEliminar: AccionesRol;

  public botonCrear: ICrearBoton;
  public search: IRegExpSearch = {
    fields: ['tipo'],
  };
  // FILTROS
  public cliente: IFiltroTabla = {
    filter: {
      field: 'idCliente',
    },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  // Cliente en cuya jerarquía cuelga la entidad: el cliente elegido y toda su
  // descendencia.
  public ancestro: IFiltroTabla = {
    filter: {
      field: 'idsAncestros',
      incluirPropio: true,
    },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };
  public filtros: IFiltroTabla[] = [];
  //Query
  public filter: IFilter<IServicio> = {};
  public populate: IPopulate[] = [
    {
      path: 'cliente',
      select: 'nombre',
    },
    {
      path: 'activo',
      select: 'identificacion',
    },
    {
      path: 'proveedor',
      select: 'nombre',
    },
    {
      path: 'usuario',
      select: 'usuario datosPersonales',
    },
  ];
  public query: IQueryParam = {
    filter: JSON.stringify(this.filter),
    populate: JSON.stringify(this.populate),
    sort: 'nombre',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
    childrenLevel: 0,
  };

  public filterVehiculos: IFilter<IServicio> = {};
  public queryVehiculos: IQueryParam = {
    filter: JSON.stringify(this.filterVehiculos),
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: false,
    childrenLevel: 0,
  };

  // Listars
  public async listar(): Promise<void> {
    const filter: IFilter<IServicio> = this.query.filter
      ? JSON.parse(this.query.filter)
      : {};
    filter.categoria = this.categoria;
    if (this.idActivo) filter.idActivo = this.idActivo;
    this.query.filter = JSON.stringify(filter);
    this.servicios$?.unsubscribe();
    this.servicios$ = this.lista2
      .servicios(this.query, 30)
      .subscribe((data) => {
        this.totalCount = data.totalCount;
        this.servicios = data.datos;
      });
  }

  // Acciones
  public verDetalle(servicio: IServicio) {
    this.dialog.open(DetalleServicioComponent, {
      data: servicio,
      width: '90vw',
      maxWidth: '800px',
      maxHeight: '90vh',
      autoFocus: false,
    });
  }

  public async crear() {
    if (this.idActivo) {
      // Embebido: ruta absoluta + activo preseleccionado en el formulario
      this.navigator.navigate([...this.rutaBase(), 'crear'], {
        queryParams: { idActivo: this.idActivo, idCliente: this.idCliente },
      });
      return;
    }
    this.navigator.navigate(['crear'], { relativeTo: this.route });
  }

  public async editar(servicio: IServicio) {
    if (this.idActivo) {
      this.navigator.navigate([...this.rutaBase(), 'editar', servicio._id]);
      return;
    }
    this.navigator.navigate(['editar', servicio._id], {
      relativeTo: this.route,
    });
  }

  /** Ruta del listado de servicios según la categoría (ver routing de cada módulo). */
  private rutaBase(): string[] {
    const modulo = this.categoria === 'Colectivo' ? 'colectivos' : 'vehiculos';
    return ['/', 'main', modulo, 'servicios', this.categoria];
  }

  public async eliminar(servicio: IServicio) {
    const res = await this.dialogService.confirm(
      'Eliminar servicio',
      `¿Querés eliminar el servicio ${servicio.tipo}?`,
    );
    if (!res) return;
    try {
      await this.service.delete(servicio._id!);
      this.helper.notifSuccess('Servicio eliminado');
      await this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  // Tabla

  private setColumnas() {
    const vehiculoLabel =
      this.categoria === 'Colectivo' ? 'Colectivo' : 'Vehículo';
    const conductorLabel =
      this.categoria === 'Colectivo' ? 'Chofer' : 'Conductor';

    this.columnas = [
      {
        header: {
          label: 'Tipo',
          sortable: true,
        },
        row: {
          field: 'tipo',
          noData: 'Sin tipo',
        },
      },
      {
        header: {
          label: 'Fecha',
          sort: 'fechaRealizacion',
          sortable: true,
        },
        row: {
          // Fecha sin hora: sólo el día
          parse: (dato) =>
            dato.fechaRealizacion
              ? fechaCelda(dato.fechaRealizacion).slice(0, 10)
              : undefined,
          noData: 'Sin reportar',
        },
      },
      {
        header: {
          label: vehiculoLabel,
        },
        row: {
          parse(dato) {
            return dato.activo?.identificacion;
          },
          noData: `Sin ${vehiculoLabel.toLowerCase()}`,
        },
      },
      {
        header: {
          label: conductorLabel,
          sortable: true,
        },
        row: {
          field: 'nombreChofer',
          noData: `Sin ${conductorLabel.toLowerCase()}`,
        },
      },
      {
        header: {
          label: 'Kilometraje',
          sortable: true,
          // La columna es parseada: sin esto el orden se pediría por el label
          sort: 'kmDelMantenimiento',
        },
        row: {
          parse(dato) {
            return dato.kmDelMantenimiento != null
              ? `${Math.round(dato.kmDelMantenimiento).toLocaleString('es-AR')} km`
              : '';
          },
          noData: 'Sin kilometraje',
        },
      },
      {
        header: {
          label: 'Costo',
          sortable: true,
          sort: 'costo',
        },
        row: {
          parse(dato) {
            return dato.costo != null
              ? `$${dato.costo.toLocaleString('es-AR', {
                  minimumFractionDigits: Number.isInteger(dato.costo) ? 0 : 2,
                  maximumFractionDigits: 2,
                })}`
              : undefined;
          },
          noData: 'Sin costo',
        },
      },
    ];
    // Cliente
    if (HelperService.getTipoCliente() !== 'Final') {
      {
        this.columnas.push({
          header: {
            label: 'Cliente',
          },
          row: {
            html: (dato) => this.clienteJerarquia.htmlNombre(dato.cliente),
            tooltip: (dato) =>
              this.clienteJerarquia.tooltipJerarquia(dato.cliente),
            noData: 'Sin cliente',
          },
        });
      }
    }

    this.columnas.push({
      header: {
        label: 'Acciones',
      },
      row: {
        acciones: [
          {
            tipo: 'material',
            color: 'primary',
            icon: 'visibility',
            tooltip: 'Ver detalle',
            click: (dato) => this.verDetalle(dato),
          },
          {
            tipo: 'material',
            color: 'primary',
            icon: 'edit',
            tooltip: 'Editar',
            click: (dato) => this.editar(dato),
            disabled: () => !this.helper.tienePermiso(this.puedeEditar),
          },
          {
            tipo: 'material',
            color: 'warn',
            icon: 'delete',
            tooltip: 'Eliminar',
            click: (dato) => this.eliminar(dato),
            disabled: () => !this.helper.tienePermiso(this.puedeEliminar),
          },
        ],
      },
    });
  }

  // Hooks

  public async ngOnInit() {
    // Embebido en el detalle de un activo: no hay params de ruta, la categoría
    // llega por Input y el filtro de cliente no aplica (ya está acotado).
    if (this.idActivo) {
      this.configurarPermisosYBoton();
      this.setColumnas();
      await this.listar();
      return;
    }

    if (HelperService.getTipoCliente() !== 'Final') {
      this.filtros.push(this.cliente);
      this.filtros.push(this.ancestro);
    }

    this.route.params.subscribe(async (params) => {
      this.categoria = params['categoria'] as CategoriaServicio;
      this.configurarPermisosYBoton();
      await this.listar();
    });

    this.setColumnas();
  }

  private configurarPermisosYBoton(): void {
    switch (this.categoria) {
      case 'Colectivo':
        this.puedeCrear = 'Colectivos - Crear servicios';
        this.puedeEditar = 'Colectivos - Editar servicios';
        this.puedeEliminar = 'Colectivos - Eliminar servicios';
        break;
      case 'Vehiculo':
        this.puedeCrear = 'Vehículos - Crear servicios';
        this.puedeEditar = 'Vehículos - Editar servicios';
        this.puedeEliminar = 'Vehículos - Eliminar servicios';
        break;
    }

    // El botón se arma recién acá, con los permisos ya resueltos
    this.botonCrear = {
      mostrar: this.helper.tienePermiso(this.puedeCrear),
      tooltip: 'Crear servicio',
      color: 'primary',
      tipo: 'material',
      icon: 'add',
      accion: () => this.crear(),
    };
  }

  public async ngOnDestroy(): Promise<void> {
    this.proveedores$?.unsubscribe();
    this.servicios$?.unsubscribe();
  }
}
